<template>
	<v-card style="position: relative;">
		<v-card-title>
			<v-row align="center">
				<v-col cols="auto">
					<v-img :src="item.icon" max-height="32" max-width="32" contain></v-img>
				</v-col>
				<v-col>
					Virtual Connections - {{item.name}}
				</v-col>
				<v-col cols="auto">
					<v-btn icon @click="close">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-col>
			</v-row>
		</v-card-title>
		<v-card-subtitle>
			Connect {{item.name}} to other organizations.
		</v-card-subtitle>
		<v-card-text style="padding-bottom: 80px; padding-right: 80px;">
			<v-row v-if="loading" justify="center">
				<v-col cols="auto">
					<v-progress-circular indeterminate color="primary"></v-progress-circular>
				</v-col>
			</v-row>
			<template v-else>
				<v-row v-if="connections.length === 0">
					<v-col cols="12" class="text-center text-body-2">
						No virtual connections found for this integration.
					</v-col>
				</v-row>
				<template v-else>
					<v-list>
						<v-list-item v-for="connection in connections" :key="connection.id">
							<v-list-item-content>
								<v-row align="center">
									<v-col cols="4">
										<v-list-item-title>{{ `${getConnectionStatus(connection)} Connection`}}</v-list-item-title>
										<v-list-item-subtitle>
											<span v-if="connection.childOrganizationName"> {{connection.childOrganizationName}}</span>
										</v-list-item-subtitle>
									</v-col>
									<v-col cols="4">
										<v-row align="center" no-gutters>
											<v-col cols="auto" class="ml-2">
												<v-switch
													v-model="connection.manualMatching"
													@change="updateManualMatching(connection)"
													dense
													inset
													hide-details
													style="margin-top: 0px;"
												></v-switch>
											</v-col>
											<v-col cols="auto">
												<span class="text-caption">Allow Matching</span>
											</v-col>
										</v-row>
									</v-col>
									<v-col cols="2" class="text-right">
										<v-btn v-if="connection.pendingCode" x-small text color="primary" @click="showCode(connection)">
											Show Code
										</v-btn>
									</v-col>
									<v-col cols="2" class="text-right">
										<v-btn icon @click="confirmDelete(connection)">
											<v-icon color="error">mdi-delete</v-icon>
										</v-btn>
									</v-col>
								</v-row>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</template>
			</template>
		</v-card-text>

		<!-- code dialog -->
		<v-dialog v-model="codeDialog" :max-width="600" persistent>
			<v-card v-if="selectedConnection">
				<v-card-title>
					Pending Code
					<v-spacer/>
					<v-btn icon @click="codeDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<v-row>
						<v-col cols="12">
							<v-textarea
								:value="selectedConnection.pendingCode"
								readonly
								outlined
								auto-grow
								label="Code"
								class="code-textarea"
							></v-textarea>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn text @click="copyCode">
						<v-icon left>mdi-content-copy</v-icon>
						Copy
					</v-btn>
					<v-btn text color="primary" @click="codeDialog = false">
						Close
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-btn
			color="primary"
			absolute
			small
			bottom
			right
			@click="confirmCreate"
			:loading="creating"
			style="margin: 6px;"
		>
			<v-icon>mdi-plus</v-icon>
			New Connection
		</v-btn>

		<!-- confirmation dialog -->
		<v-dialog v-model="confirmDialog" :max-width="400" persistent>
			<v-card>
				<v-card-title>
					{{ confirmAction === 'create' ? 'Create New Virtual Connection' : 'Delete Virtual Connection' }}
				</v-card-title>
				<v-card-text>
					{{ confirmAction === 'create'
						? 'Are you sure you want to create a new virtual connection for this integration?'
						: 'Are you sure you want to delete this virtual connection? This action cannot be undone.'
					}}
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn text @click="confirmDialog = false">
						Cancel
					</v-btn>
					<v-btn
						:color="confirmAction === 'create' ? 'primary' : 'error'"
						@click="handleConfirmAction"
					>
						{{ confirmAction === 'create' ? 'Create' : 'Delete' }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
	name: "VirtualConnectionsDialog",
	props: {
		item: {
			type: Object,
			required: true
		}
	},
	computed: {
		...mapState('virtualConnections', ['connections', 'loading'])
	},
	async mounted() {
		await this.loadConnections(this.item.integrationId);
	},
	methods: {
		...mapActions('virtualConnections', ['loadConnections', 'createConnection', 'deleteConnection', 'updateConnection']),
		close() {
			this.$emit('close');
		},
		confirmCreate() {
			this.confirmAction = 'create';
			this.confirmDialog = true;
		},
		confirmDelete(connection) {
			this.confirmAction = 'delete';
			this.connectionToDelete = connection;
			this.confirmDialog = true;
		},
		async handleConfirmAction() {
			this.confirmDialog = false;
			if (this.confirmAction === 'create') {
				await this.handleCreateConnection();
			} else {
				await this.handleDeleteConnection();
			}
		},
		async handleCreateConnection() {
			this.creating = true;
			try {
				await this.createConnection(this.item.integrationId);
				await this.loadConnections(this.item.integrationId);
			} catch (error) {
				console.error('Failed to create connection:', error);
			} finally {
				this.creating = false;
			}
		},
		async handleDeleteConnection() {
			this.creating = true;
			try {
				await this.deleteConnection(this.connectionToDelete.guid);
				await this.loadConnections(this.item.integrationId);
			} catch (error) {
				console.error('Failed to delete connection:', error);
			} finally {
				this.creating = false;
				this.connectionToDelete = null;
			}
		},
		showCode(connection) {
			this.selectedConnection = connection;
			this.codeDialog = true;
		},
		getConnectionStatus(connection) {
			if (connection.pendingCode && !connection.childOrganizationId) {
				return 'Pending';
			}
			return connection.status || 'Active';
		},
		getStatusColor(connection) {
			const status = this.getConnectionStatus(connection);
			switch(status) {
				case 'Pending':
					return 'orange';
				case 'Active':
					return 'green';
			}
		},
		async copyCode() {
			if (this.selectedConnection && this.selectedConnection.pendingCode) {
				try {
					await navigator.clipboard.writeText(this.selectedConnection.pendingCode);
				} catch (err) {
					console.error('Failed to copy code:', err);
				}
			}
		},
		async updateManualMatching(connection) {
			try {
				await this.updateConnection({
					guid: connection.guid,
					settings: { manualMatching: connection.manualMatching }
				});
			} catch (error) {
				console.error('Failed to update manual matching:', error);
				// Revert the toggle on error
				connection.manualMatching = !connection.manualMatching;
			}
		}
	},
	data() {
		return {
			codeDialog: false,
			selectedConnection: null,
			creating: false,
			confirmDialog: false,
			confirmAction: 'create',
			connectionToDelete: null
		};
	}
};
</script>

<style scoped>
.code-textarea textarea {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}
</style>